import React, { useEffect, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { Plus, X, Printer, Trash2, Tag, ScanSearch, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import JsBarcode from "jsbarcode";
import QRCode from "qrcode";
import { ensureArabicFont, pdfText, hasArabic } from "@/lib/pdfFonts";
import { CountrySelect, ProvinceSelect } from "@/components/GeoSelect";

const EMPTY = {
  remitente_nombre: "", remitente_direccion: "", remitente_telefono: "", remitente_email: "",
  receptor_nombre: "", receptor_direccion: "", receptor_telefono: "", receptor_email: "",
  numero_paquetes: 1, peso_total: 0, cbm: 0, dimensiones: "", referencia: "", contenido: "", notas: "",
  modo: "maritimo", client_id: "", destino: "",
  transportista: "", tracking_transportista: "", tipo_servicio: "grupaje",
  origen_pais: "", origen_ciudad: "", destino_pais: "", destino_ciudad: "",
  largo: 0, ancho: 0, alto: 0,
  warehouse_id: "", ubicacion: "",
  fecha_registro: "",
};

const MODES = [
  { v: "aereo", k: "air", color: "bg-sky-600" },
  { v: "terrestre", k: "road", color: "bg-amber-600" },
  { v: "maritimo", k: "sea", color: "bg-cyan-700" },
  { v: "express", k: "express", color: "bg-red-600" },
];

const MODE_PDF = {
  aereo: "AEREO / AIR", terrestre: "TERRESTRE / ROAD", maritimo: "MARITIMO / SEA", express: "EXPRESS",
};

const FLOW_TONE = {
  clasificado: "bg-amber-100 text-amber-700", en_salida: "bg-indigo-100 text-indigo-700",
  transito_hub: "bg-blue-100 text-blue-700", recibido_hub: "bg-violet-100 text-violet-700",
  verificado_hub: "bg-emerald-100 text-emerald-700", en_master: "bg-teal-100 text-teal-700",
  master_cerrado: "bg-slate-800 text-white", entregado: "bg-emerald-600 text-white",
};

export default function Labels() {
  const { api, user } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [countries, setCountries] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  // Only hub-side roles can register a package directly in warehouse stock;
  // for sales/branch users this would 403 on /packages/hub-direct.
  const canHubDirect = ["admin", "operaciones", "almacen"].includes(user.role);
  const [hubDirect, setHubDirect] = useState(user.role === "almacen");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [stalled, setStalled] = useState({ count: 0, dias: 3, packages: [] });
  const [stalledDias, setStalledDias] = useState(3);
  const [onlyStalled, setOnlyStalled] = useState(false);

  const stalledMap = new Map(stalled.packages.map(p => [p.codigo, p.dias_parado]));
  const visible = rows.filter(l => {
    if (onlyStalled && !stalledMap.has(l.codigo)) return false;
    if (filter && (l.estado_flujo || "clasificado") !== filter) return false;
    if (!search) return true;
    const hay = [l.codigo, l.tracking_transportista, l.transportista, l.remitente_nombre, l.receptor_nombre,
      l.destino_ciudad, l.destino_pais, l.referencia, l.office_exit_code, l.master_code].join(" ").toLowerCase();
    return hay.includes(search.toLowerCase());
  });

  const load = () => api.get("/labels").then(r => setRows(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/packages/stalled", { params: { dias: stalledDias } }).then(r => setStalled(r.data)).catch(() => {});
    /* eslint-disable-next-line */
  }, [stalledDias]);
  useEffect(() => {
    load();
    api.get("/clientes").then(r => setClients(r.data)).catch(() => {});
    api.get("/countries").then(r => setCountries(r.data || [])).catch(() => {});
    api.get("/warehouses").then(r => setWarehouses(r.data || [])).catch(() => {});
    api.get("/packages/stalled", { params: { dias: stalledDias } }).then(r => setStalled(r.data)).catch(() => {});
    /* eslint-disable-next-line */
  }, []);

  const autoCbm = (f) => {
    const { largo, ancho, alto, numero_paquetes } = f;
    if (largo && ancho && alto) return +(((+largo) * (+ancho) * (+alto) * Math.max(1, +numero_paquetes || 1)) / 1000000).toFixed(4);
    return f.cbm;
  };

  const setDim = (patch) => setForm(f => { const n = { ...f, ...patch }; return { ...n, cbm: autoCbm(n) }; });

  const pickClient = (id, side) => {
    const c = clients.find(x => x.id === id);
    if (!c) return;
    const addr = [c.direccion, c.ciudad, c.provincia].filter(Boolean).join(", ");
    // Picking a client also fills in its country/city, so the destination is always consistent
    if (side === "rem") {
      setForm(f => ({
        ...f, client_id: id, remitente_nombre: c.empresa || c.fullname, remitente_direccion: addr,
        remitente_telefono: c.telefono || "", remitente_email: c.email || "",
        origen_pais: c.pais || f.origen_pais, origen_ciudad: c.ciudad || c.provincia || f.origen_ciudad,
      }));
    } else {
      setForm(f => ({
        ...f, client_id: id, receptor_nombre: c.empresa || c.fullname, receptor_direccion: addr,
        receptor_telefono: c.telefono || "", receptor_email: c.email || "",
        destino_pais: c.pais || f.destino_pais, destino_ciudad: c.ciudad || c.provincia || f.destino_ciudad,
      }));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    // Safety: never POST to /packages/hub-direct from a role the backend
    // rejects (only admin / operaciones / almacen can). Otherwise the user
    // sees a 403 "Forbidden" that is easy to confuse with "no puede crear".
    const useHub = hubDirect && canHubDirect;
    try {
      const { data } = useHub
        ? await api.post("/packages/hub-direct", { ...form, warehouse_id: form.warehouse_id || user.warehouse_id || "" })
        : await api.post("/labels", form);
      toast.success(`${t("saved")} · ${data.codigo}`);
      setForm(EMPTY); setShowForm(false);
      load();
      printLabel(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try { await api.delete(`/labels/${id}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const printLabel = async (l) => {
    // 4x6" thermal label = 288 x 432 pt (DHL/UPS/FedEx standard)
    const doc = new jsPDF({ unit: "pt", format: [288, 432] });
    const w = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();

    const anyArabic = [l.remitente_nombre, l.remitente_direccion, l.receptor_nombre, l.receptor_direccion, l.contenido, l.notas, l.destino_ciudad].some(hasArabic);
    if (anyArabic) await ensureArabicFont(doc);

    // QR opens the public traceability of this exact package on any phone
    let qrDataUrl = null;
    const traceUrl = `${window.location.origin}/track/${encodeURIComponent(l.codigo || "")}`;
    try { qrDataUrl = await QRCode.toDataURL(traceUrl, { margin: 0, width: 240 }); }
    catch (e) { console.warn("Label QR failed:", e); }

    let barcodeDataUrl = null;
    const barcodeValue = (l.codigo || "").trim();
    if (barcodeValue) {
      try {
        const canvas = document.createElement("canvas");
        JsBarcode(canvas, barcodeValue, { format: "CODE128", displayValue: false, height: 70, margin: 0, width: 2.2 });
        barcodeDataUrl = canvas.toDataURL("image/png");
      } catch (e) { console.warn("Label barcode failed:", e); }
    }

    const img = new Image();
    img.onload = () => render(img);
    img.onerror = () => render(null);
    img.src = "/logo.png";

    function render(logo) {
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 0, w, 52, "F");
      if (logo) {
        try { doc.addImage(logo, "PNG", 10, 10, 52, 32); } catch (e) { console.warn("Label logo embed failed:", e); }
      }
      doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(255, 255, 255);
      doc.text("MEOS CARGO", w - 12, 25, { align: "right" });
      doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(203, 213, 225);
      doc.text("SHIPPING LABEL", w - 12, 40, { align: "right" });

      // ==== TRANSPORT TYPE — must be immediately visible on the box ====
      const tone = { aereo: [3, 105, 161], terrestre: [180, 83, 9], maritimo: [14, 116, 144], express: [185, 28, 28] }[l.modo] || [185, 28, 28];
      doc.setFillColor(...tone);
      doc.rect(0, 52, w, 30, "F");
      doc.setFont("helvetica", "bold"); doc.setFontSize(17); doc.setTextColor(255, 255, 255);
      doc.text(MODE_PDF[l.modo] || "EXPRESS", 12, 74);
      doc.setFontSize(8);
      doc.text(new Date(l.created_at || Date.now()).toLocaleDateString(), w - 12, 74, { align: "right" });

      // ==== CARRIER + CARRIER TRACKING (never mixed with our internal code) ====
      let y = 96;
      doc.setFillColor(241, 245, 249); doc.rect(8, y - 12, w - 16, 34, "F");
      doc.setFont("helvetica", "bold"); doc.setFontSize(6); doc.setTextColor(100, 116, 139);
      doc.text("CARRIER", 14, y - 2); doc.text("CARRIER TRACKING", 96, y - 2);
      doc.setFontSize(9); doc.setTextColor(15, 23, 42);
      doc.text((l.transportista || "—").toUpperCase(), 14, y + 12);
      doc.setFont("courier", "bold"); doc.setFontSize(10);
      doc.text(l.tracking_transportista || "—", 96, y + 12);
      y += 34;

      doc.setFont("helvetica", "bold"); doc.setFontSize(6); doc.setTextColor(100, 116, 139);
      doc.text("FROM · SENDER", 12, y);
      doc.setDrawColor(226, 232, 240); doc.setLineWidth(0.5); doc.line(12, y + 3, w - 12, y + 3);
      y += 14;
      doc.setFontSize(10); doc.setTextColor(15, 23, 42);
      pdfText(doc, l.remitente_nombre || "-", 12, y);
      y += 11;
      doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.setTextColor(71, 85, 105);
      const rAddr = doc.splitTextToSize([l.remitente_direccion, [l.origen_ciudad, l.origen_pais].filter(Boolean).join(", ")].filter(Boolean).join(" · "), w - 24);
      rAddr.slice(0, 2).forEach((line) => { pdfText(doc, line, 12, y); y += 9; });

      y += 4;
      doc.setDrawColor(15, 23, 42); doc.setLineWidth(1.2);
      doc.rect(8, y, w - 16, 80);
      doc.setFillColor(15, 23, 42);
      doc.rect(8, y, w - 16, 14, "F");
      doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(255, 255, 255);
      doc.text("TO · RECEIVER", 12, y + 10);
      let ry = y + 27;
      doc.setFontSize(12); doc.setTextColor(15, 23, 42);
      pdfText(doc, l.receptor_nombre || "-", 12, ry);
      ry += 13;
      doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(30, 41, 59);
      const rc = doc.splitTextToSize(l.receptor_direccion || "", w - 28);
      rc.slice(0, 2).forEach((line) => { pdfText(doc, line, 12, ry); ry += 10; });
      doc.setFont("helvetica", "bold"); doc.setFontSize(11);
      pdfText(doc, [l.destino_ciudad, l.destino_pais].filter(Boolean).join(" · ") || l.destino || "", 12, ry + 2);
      if (l.receptor_telefono) { doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.text("Tel: " + l.receptor_telefono, w - 14, ry + 2, { align: "right" }); }
      y += 92;

      doc.setFillColor(241, 245, 249); doc.rect(8, y, w - 16, 34, "F");
      doc.setFont("helvetica", "bold"); doc.setFontSize(6); doc.setTextColor(100, 116, 139);
      doc.text("UNITS", 14, y + 10); doc.text("WEIGHT", 66, y + 10); doc.text("CBM", 140, y + 10); doc.text("DIMS (cm)", 196, y + 10);
      doc.setFontSize(14); doc.setTextColor(15, 23, 42);
      doc.text(String(l.numero_paquetes || 1), 14, y + 27);
      doc.text((l.peso_total || 0) + " kg", 66, y + 27);
      doc.setFontSize(12);
      doc.text(String(l.cbm || 0), 140, y + 27);
      doc.setFontSize(9);
      doc.text(l.largo ? `${l.largo}x${l.ancho}x${l.alto}` : (l.dimensiones || "-"), 196, y + 27);
      y += 42;

      doc.setDrawColor(15, 23, 42); doc.setLineWidth(1.5);
      doc.rect(8, y, w - 16, 84);
      doc.setFont("helvetica", "bold"); doc.setFontSize(6); doc.setTextColor(120, 120, 120);
      doc.text("INTERNAL TRACKING", 14, y + 10);
      if (barcodeDataUrl) {
        try { doc.addImage(barcodeDataUrl, "PNG", 14, y + 14, w - 110, 44); } catch (e) { console.warn("Label barcode embed failed:", e); }
      }
      if (qrDataUrl) {
        try { doc.addImage(qrDataUrl, "PNG", w - 82, y + 14, 62, 62); } catch (e) { console.warn("Label QR embed failed:", e); }
        doc.setFont("helvetica", "bold"); doc.setFontSize(5); doc.setTextColor(100, 116, 139);
        doc.text("SCAN QR · TRACK", w - 51, y + 82, { align: "center" });
      }
      doc.setFont("courier", "bold"); doc.setFontSize(12); doc.setTextColor(0, 0, 0);
      doc.text(l.codigo || "-", 14 + (w - 110) / 2, y + 70, { align: "center" });
      y += 92;

      doc.setFont("helvetica", "bold"); doc.setFontSize(6); doc.setTextColor(100, 116, 139);
      doc.text("OFFICE", 12, y); doc.text("CONTENTS", 74, y);
      doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(30, 41, 59);
      pdfText(doc, l.oficina || l.oficina_recepcion || "—", 12, y + 10);
      const ctLines = doc.splitTextToSize(l.contenido || "—", w - 90);
      ctLines.slice(0, 2).forEach((line, i) => pdfText(doc, line, 74, y + 10 + i * 9));

      doc.setDrawColor(226, 232, 240); doc.line(8, H - 22, w - 8, H - 22);
      doc.setFont("helvetica", "normal"); doc.setFontSize(6); doc.setTextColor(148, 163, 184);
      doc.text("meoscargo.com · " + (l.tipo_servicio || "") + " · " + new Date().toLocaleString(), w / 2, H - 10, { align: "center" });

      doc.save(`Label_${l.codigo}.pdf`);
      toast.success("PDF");
    }
  };

  const inp = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600";
  const lbl = "block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide";

  return (
    <div className="space-y-4" data-testid="labels-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2"><Tag className="w-5 h-5 text-red-600" /> {t("packages")}</h2>
          <p className="text-sm text-slate-500">{visible.length} / {rows.length}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input data-testid="pkg-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("search")} className="px-3 py-2 border border-slate-300 rounded-lg text-sm" />
          <select data-testid="lbl-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm">
            <option value="">{t("all")}</option>
            {["clasificado", "en_salida", "transito_hub", "recibido_hub", "verificado_hub", "en_master", "master_cerrado", "entregado"].map(s => (
              <option key={s} value={s}>{t(`fs_${s}`)}</option>
            ))}
          </select>
          <button data-testid="new-label-btn" onClick={() => { setForm(EMPTY); setShowForm(true); }} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2">
            <Plus className="w-4 h-4" /> {t("new_package")}
          </button>
        </div>
      </div>

      <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-2.5">{t("packages_flow_hint")}</p>

      {(
        <div className={`rounded-xl p-3 flex flex-wrap items-center gap-3 border-2 ${stalled.count > 0 ? "bg-orange-50 border-orange-300" : "bg-emerald-50 border-emerald-200"}`} data-testid="stalled-alert">
          <AlertTriangle className={`w-5 h-5 shrink-0 ${stalled.count > 0 ? "text-orange-600" : "text-emerald-600"}`} />
          <div className="flex-1 min-w-[220px]">
            <p className={`text-sm font-semibold ${stalled.count > 0 ? "text-orange-900" : "text-emerald-900"}`} data-testid="stalled-count">
              {stalled.count > 0 ? t("stalled_alert").replace("{n}", stalled.count).replace("{d}", stalled.dias) : t("stalled_none").replace("{d}", stalled.dias)}
            </p>
            <p className="text-[11px] text-orange-700 truncate">
              {stalled.packages.slice(0, 4).map(p => `${p.codigo} (${p.dias_parado}d)`).join(" · ")}{stalled.packages.length > 4 ? " …" : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select data-testid="stalled-dias" value={stalledDias} onChange={(e) => setStalledDias(+e.target.value)} className="px-2 py-1.5 border border-orange-300 rounded-lg text-xs bg-white">
              {[1, 2, 3, 5, 7, 10, 15, 30].map(d => <option key={d} value={d}>{d} {t("days")}</option>)}
            </select>
            <button data-testid="stalled-toggle" disabled={stalled.count === 0} onClick={() => setOnlyStalled(v => !v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${onlyStalled ? "bg-orange-600 text-white border-orange-600" : "bg-white text-orange-800 border-orange-300"}`}>
              {onlyStalled ? t("all") : t("view_only_stalled")}
            </button>
          </div>
        </div>
      )}

      {visible.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center text-slate-400" data-testid="no-packages">
          <Tag className="w-14 h-14 mx-auto opacity-30 mb-3" />
          <p>{t("no_packages")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {visible.map(l => {
            const flow = l.estado_flujo || "clasificado";
            const mode = MODES.find(m => m.v === l.modo) || MODES[3];
            const rec = l.recibido;
            return (
              <div key={l.id} data-testid={`lbl-${l.id}`} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className={`${mode.color} px-3 py-1.5 flex items-center justify-between`}>
                  <span className="text-[11px] font-bold uppercase tracking-widest text-white">{t(mode.k)}</span>
                  <span data-testid={`lbl-flow-${l.codigo}`} className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${FLOW_TONE[flow] || "bg-white/20 text-white"}`}>{t(`fs_${flow}`) || flow}</span>
                </div>
                <div className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="font-mono font-semibold text-sm">{l.codigo}</div>
                      <div className="text-[10px] text-slate-400 uppercase tracking-wider tabular-nums">{new Date(l.created_at).toLocaleDateString()} · {l.oficina || "—"}</div>
                    </div>
                    <div className="flex gap-1">
                      <button data-testid={`lbl-trace-${l.codigo}`} title={t("timeline")} onClick={() => nav(`/trazabilidad?code=${encodeURIComponent(l.codigo)}`)} className="p-1.5 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-md"><ScanSearch className="w-4 h-4" /></button>
                      <button data-testid={`lbl-print-${l.id}`} onClick={() => printLabel(l)} className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-md"><Printer className="w-4 h-4" /></button>
                      {flow === "clasificado" && <button data-testid={`lbl-del-${l.id}`} onClick={() => remove(l.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  </div>
                  <div className="text-xs space-y-1">
                    {l.tracking_transportista && (
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-900 text-white">{l.transportista || t("carrier")}</span>
                        <b className="font-mono">{l.tracking_transportista}</b>
                      </div>
                    )}
                    <div><span className="text-slate-500">{t("sender")}:</span> <b>{l.remitente_nombre}</b></div>
                    <div><span className="text-slate-500">{t("receiver")}:</span> <b>{l.receptor_nombre}</b></div>
                    <div className="text-slate-500">{[l.origen_ciudad, l.destino_ciudad].filter(Boolean).join(" → ") || l.destino || "—"}</div>
                    <div className="text-slate-600">{l.numero_paquetes} {t("units").toLowerCase()} · {l.peso_total} kg{l.cbm ? ` · ${l.cbm} CBM` : ""}</div>
                    {rec && (
                      <div className="text-[11px] bg-violet-50 border border-violet-200 rounded p-1.5 text-violet-800">
                        {t("received_val")}: {rec.unidades} · {rec.peso} kg · {rec.cbm} CBM
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
                      {l.office_exit_code && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">{l.office_exit_code}</span>}
                      {l.master_code && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-teal-50 text-teal-700">{l.master_code}</span>}
                      {l.cliente_numero_cuenta && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{l.cliente_numero_cuenta}</span>}
                      {stalledMap.has(l.codigo) && (
                        <span data-testid={`stalled-badge-${l.codigo}`} className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-orange-600 text-white">
                          {stalledMap.get(l.codigo)} {t("days")} · {t("no_movement")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-3xl p-6 space-y-4 max-h-[92vh] overflow-y-auto" data-testid="package-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-semibold text-lg">{t("new_package")}</h3>
              <button type="button" onClick={() => setShowForm(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>

            <div className={`rounded-lg border-2 p-3 ${hubDirect ? "border-violet-300 bg-violet-50/50" : "border-slate-200 bg-slate-50"} ${canHubDirect ? "" : "hidden"}`}>
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <input type="checkbox" data-testid="lf-hub-direct" checked={hubDirect} onChange={(e) => setHubDirect(e.target.checked)} />
                {t("hub_direct")}
              </label>
              <p className="text-[11px] text-slate-500 mt-1">{t("hub_direct_hint")}</p>
              {hubDirect && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className={lbl}>{t("warehouse")} *</label>
                    <select required data-testid="lf-warehouse" value={form.warehouse_id} onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })} className={inp}>
                      <option value="">—</option>
                      {warehouses.map(w => <option key={w.id} value={w.id}>{w.nombre}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={lbl}>{t("location")}</label>
                    <input data-testid="lf-ubicacion" value={form.ubicacion} onChange={(e) => setForm({ ...form, ubicacion: e.target.value })} placeholder="A-01-03" className={inp} />
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className={lbl}>{t("transport_mode")} *</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="lf-modo">
                {MODES.map(({ v, k }) => (
                  <button key={v} type="button" data-testid={`lf-modo-${v}`} onClick={() => setForm({ ...form, modo: v })}
                    className={`py-2 rounded-lg text-sm font-semibold border transition-colors ${form.modo === v ? "bg-red-600 text-white border-red-600" : "bg-white text-slate-600 border-slate-300"}`}>{t(k)}</button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 border border-slate-900/10 bg-slate-50 rounded-lg p-3">
              <div><label className={lbl}>{t("carrier")}</label><input data-testid="lf-carrier" value={form.transportista} onChange={(e) => setForm({ ...form, transportista: e.target.value })} placeholder="DHL / DPD ..." className={inp} /></div>
              <div><label className={lbl}>{t("carrier_tracking")}</label><input data-testid="lf-carrier-tracking" value={form.tracking_transportista} onChange={(e) => setForm({ ...form, tracking_transportista: e.target.value })} className={`${inp} font-mono`} /></div>
              <div><label className={lbl}>{t("service_type")}</label><input data-testid="lf-service" value={form.tipo_servicio} onChange={(e) => setForm({ ...form, tipo_servicio: e.target.value })} className={inp} /></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3 border border-slate-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600">{t("sender")}</h4>
                  <select data-testid="lf-rem-pick" onChange={(e) => pickClient(e.target.value, "rem")} className="text-xs border border-slate-200 rounded px-2 py-1 bg-slate-50">
                    <option value="">{t("client")}...</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.fullname} {c.empresa ? `(${c.empresa})` : ""}</option>)}
                  </select>
                </div>
                <input required data-testid="lf-rem-nombre" placeholder={t("fullname")} value={form.remitente_nombre} onChange={(e) => setForm({ ...form, remitente_nombre: e.target.value })} className={inp} />
                <input placeholder={t("address")} value={form.remitente_direccion} onChange={(e) => setForm({ ...form, remitente_direccion: e.target.value })} className={inp} />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className={lbl}>{t("origin_country")}</label>
                    <CountrySelect countries={countries} testid="lf-origen-pais" className={inp}
                      value={form.origen_pais} onChange={(v) => setForm({ ...form, origen_pais: v, origen_ciudad: "" })} />
                  </div>
                  <div>
                    <label className={lbl}>{t("origin_city")}</label>
                    <ProvinceSelect countries={countries} country={form.origen_pais} testid="lf-origen-ciudad" className={inp}
                      value={form.origen_ciudad} onChange={(v) => setForm({ ...form, origen_ciudad: v })} />
                  </div>
                </div>
                <input placeholder={t("phone")} value={form.remitente_telefono} onChange={(e) => setForm({ ...form, remitente_telefono: e.target.value })} className={inp} />
              </div>
              <div className="space-y-3 border border-red-200 bg-red-50/40 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-red-700">{t("receiver")}</h4>
                  <select data-testid="lf-rec-pick" onChange={(e) => pickClient(e.target.value, "rec")} className="text-xs border border-red-200 rounded px-2 py-1 bg-white">
                    <option value="">{t("client")}...</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.fullname} {c.empresa ? `(${c.empresa})` : ""}</option>)}
                  </select>
                </div>
                <input required data-testid="lf-rec-nombre" placeholder={t("fullname")} value={form.receptor_nombre} onChange={(e) => setForm({ ...form, receptor_nombre: e.target.value })} className={inp} />
                <input placeholder={t("address")} value={form.receptor_direccion} onChange={(e) => setForm({ ...form, receptor_direccion: e.target.value })} className={inp} />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className={lbl}>{t("dest_country")} *</label>
                    <CountrySelect required countries={countries} testid="lf-dest-pais" className={inp}
                      value={form.destino_pais} onChange={(v) => setForm({ ...form, destino_pais: v, destino_ciudad: "" })} />
                  </div>
                  <div>
                    <label className={lbl}>{t("dest_city")} *</label>
                    <ProvinceSelect required countries={countries} country={form.destino_pais} testid="lf-dest-ciudad" className={inp}
                      value={form.destino_ciudad} onChange={(v) => setForm({ ...form, destino_ciudad: v })} />
                  </div>
                </div>
                <input placeholder={t("phone")} value={form.receptor_telefono} onChange={(e) => setForm({ ...form, receptor_telefono: e.target.value })} className={inp} />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
              <div><label className={lbl}>{t("units")}</label><input required type="number" min="1" data-testid="lf-paq" value={form.numero_paquetes} onChange={(e) => setDim({ numero_paquetes: +e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("weight_kg")}</label><input type="number" step="0.1" data-testid="lf-peso" value={form.peso_total} onChange={(e) => setForm({ ...form, peso_total: +e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("length_cm")}</label><input type="number" step="0.1" data-testid="lf-largo" value={form.largo} onChange={(e) => setDim({ largo: +e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("width_cm")}</label><input type="number" step="0.1" data-testid="lf-ancho" value={form.ancho} onChange={(e) => setDim({ ancho: +e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("height_cm")}</label><input type="number" step="0.1" data-testid="lf-alto" value={form.alto} onChange={(e) => setDim({ alto: +e.target.value })} className={inp} /></div>
              <div><label className={lbl}>CBM</label><input type="number" step="0.0001" data-testid="lf-cbm" value={form.cbm} onChange={(e) => setForm({ ...form, cbm: +e.target.value })} className={inp} /></div>
            </div>
            <p className="text-[11px] text-slate-400 -mt-2">{t("auto_cbm_hint")}</p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><label className={lbl}>{t("reference")}</label><input value={form.referencia} onChange={(e) => setForm({ ...form, referencia: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("content")}</label><input value={form.contenido} onChange={(e) => setForm({ ...form, contenido: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("observations")}</label><input data-testid="lf-notas" value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} className={inp} /></div>
            </div>

            {user.role === "admin" && (
              <div className="rounded-lg border-2 border-amber-300 bg-amber-50/60 p-3">
                <label className="flex items-center gap-2 text-sm font-semibold text-amber-900">
                  <input type="checkbox" data-testid="lf-backdate-toggle" checked={!!form.fecha_registro} onChange={(e) => setForm({ ...form, fecha_registro: e.target.checked ? new Date().toISOString().slice(0,10) : "" })} />
                  {t("backdate_label") || "Fecha antigua (admin)"}
                </label>
                <p className="text-[11px] text-amber-700 mt-1">{t("backdate_hint") || "Solo admin — registra el paquete con una fecha pasada"}</p>
                {form.fecha_registro && (
                  <input
                    type="date"
                    data-testid="lf-backdate-date"
                    value={form.fecha_registro}
                    max={new Date().toISOString().slice(0,10)}
                    onChange={(e) => setForm({ ...form, fecha_registro: e.target.value })}
                    className={`${inp} mt-2 bg-white max-w-xs`}
                  />
                )}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="flex-1 border border-slate-300 py-2.5 rounded-lg font-semibold text-sm">{t("cancel")}</button>
              <button type="submit" data-testid="lf-submit" className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg font-semibold text-sm">{t("save_and_print")}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
